<template>
  <div class="page records">
    <WhenOrientation value="portrait">
      <div style="margin: 20px; padding: 20px; border-radius: 10px; text-align: center; background: rgba(50, 100, 255, 0.1);">
        Rotate your device into <span style="font-weight: bold;">landscape orientation</span> to view records.
      </div>
    </WhenOrientation>
    <WhenOrientation value="landscape">
      <div class="readonly-warning" v-if="readonlyDatabase && store.showAdvanced">
        Records are read-only
      </div>
      <div style="display: flex; min-height: 0; flex-grow: 1; padding: 8px; gap: 12px;">
        <records-menu ref="recordsMenu"
          v-show="showingMenu"
          v-model:tags="tags"
          v-model:bins="bins"
          v-model:events="events"
          v-model:readonlyDatabase="readonlyDatabase"
          v-model:showingMenu="showingMenu"
          :isTagSelectable="isTagSelectable"
          :normalized_timeFrom="normalized_timeFrom"
          :normalized_timeTo="normalized_timeTo"
          :viewType="viewType"
          @showGraph="showGraph()"
          @showTable="showTable()"
        >
          <template #RecordsMenuButton>
            <RecordsMenuButton v-model:showingMenu="showingMenu" :deleteAllRecords :deleteSelectedRecords :deleteArchivedRecords></RecordsMenuButton>
          </template>
        </records-menu>
        <component v-if="menuDataLoaded" class="view-component" ref="viewComponent"
          :is="viewComponent" 
          :tags="tags"
          :bins="bins"
          :relevantBins="relevantBins"
          :selectedBins="selectedBins"
          :controls="relevantSelectedControls"
          :events="selectedEvents"
          :timeSegments="timeSegments"
          :selectableBins="selectableBins"
          :isTagSelectable="isTagSelectable"
          :recordsMenuRefresh="recordsMenuRefresh"
          :selectedTemperatureUnit="selectedTemperatureUnit"
          :saveSelections="saveSelections"
          v-model:normalized_timeFrom="normalized_timeFrom"
          v-model:normalized_timeTo="normalized_timeTo"
          v-model:showingMenu="showingMenu"
        >
          <template #RecordsMenuButton v-if="!showingMenu">
            <RecordsMenuButton v-model:showingMenu="showingMenu" :deleteAllRecords :deleteSelectedRecords :deleteArchivedRecords></RecordsMenuButton>
          </template>
          <template #date-from>
            <RecordsDatePickerButton placeholderLabel="Oldest record"
              :dateToLabel="dateFrom"
              v-model:dateFrom="dateFrom"
              v-model:dateTo="dateTo"
            >
              <template #prefix v-if="showingDesktopUi">
                From
              </template>
            </RecordsDatePickerButton>
          </template>
          <template #date-to>
            <RecordsDatePickerButton placeholderLabel="Newest record"
              :dateToLabel="dateTo"
              v-model:dateFrom="dateFrom"
              v-model:dateTo="dateTo"
            >
              <template #prefix v-if="showingDesktopUi">
                To
              </template>
            </RecordsDatePickerButton>
          </template>
          <template #selectedTemperatureUnit>
            <Dropdown v-if="showingSelectedTemperatureUnit" v-model="selectedTemperatureUnit" :options="selectedTemperatureUnitOptions" style="display: flex; align-items: center;">
              <template #value="{value}">
                <span v-html="unitHtml(value)"></span>
              </template>
              <template #option="{option}">
                <span v-html="unitHtml(option)"></span>
              </template>
            </Dropdown>
          </template>
        </component>
      </div>
    </WhenOrientation>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import RecordsMenu from "./RecordsMenu.vue";
import RecordsMenuButton from "./RecordsMenuButton.vue";
const RecordsGraph = defineAsyncComponent(() => import("./RecordsGraph.vue"));
import RecordsTable from "./RecordsTable.vue";
import RecordsDatePickerButton from "./RecordsDatePickerButton.vue";
import WhenOrientation from "./WhenOrientation.vue";
import { promiseTimeout } from "@vueuse/core";
import { showingDesktopUi } from "./store.js";
import { keyBy, padStart, sortBy } from "./utility.js";
import { unitHtml } from "./units.js";
import { zoneDate, dezoneDate } from "./timezones.js";
import { normalizeLeftTimestamp, normalizeRightTimestamp, denormalizeTimestamp } from "./timestamps.js";
import { generateColor } from "./RecordsColorUtility";

export default {
  components: { RecordsMenu, RecordsMenuButton, RecordsGraph, RecordsTable, RecordsDatePickerButton, WhenOrientation },
  setup() {
    return {
      showingDesktopUi
    };
  },
  data() {
    let selectedTime = JSON.parse(localStorage.getItem("records.selectedTime"));
    let normalized_timeFrom = selectedTime?.begin || null;
    let normalized_timeTo = selectedTime?.end || null;

    return {
      store,
      showingMenu: true,
      someDate: null,
      tags: [],
      bins: [],
      events: [],
      selectedTagIds: [],
      normalized_timeFrom,
      normalized_timeTo,
      viewType: localStorage.getItem("records.viewType") || "table",
      selectedTemperatureUnit: store.sessions[0].system.temperatureUnit.value,
      selectedTemperatureUnitOptions: [ "fahrenheit", "celsius" ],
      selectedUnitOptionsPt: { input: { style: "font-size: small;" } },
      readonlyDatabase: false
    };
  },
  computed: {
    tagsMappedByDatabaseId() {
      let system = store.sessions[0].system;
      let tags = [ system, ... system.$allChildren() ];
      return keyBy(tags, tag => tag.databaseId);
    },
    menuDataLoaded() {
      return this.bins.length > 0;
    },
    viewComponent() {
      switch(this.viewType) {
        case "graph": return RecordsGraph;
        case "table": return RecordsTable;
      };
    },
    controls() {
      return this.bins.flatMap(bin => [
        ... bin.aggrs, 
        ... bin.aggrs.flatMap(aggr => aggr.sensors),
        ... bin.outputs
      ]);
    },
    unusedColors() {
      let colorList = ["#007bff", "#28a745", "#c91f1f", "#fd7e14", "#3e29a8", "#6c757d"];
      return colorList.filter(color => !this.controls.some(control => control.color == color));
    },
    selectableBins() {
      return this.bins.filter(this.isTagSelectable);
    },
    selectedBins() {
      return this.bins.filter(bin => bin.selected);
    },
    relevantBins() {
      if (this.events.some(event => event.selected)) {
        return this.selectedBins;
      }
      else {
        return this.selectedBins.filter(bin => {
          let sensors = bin.aggrs.flatMap(aggr => aggr.sensors);
          let controls = [ ... bin.aggrs, ... sensors, ... bin.outputs ];
          return controls.some(control => control.selected);
        });
      }
    },
    selectedControls() {
      return this.controls.filter(control => control.selected);
    },
    showingSelectedTemperatureUnit() {
      return this.selectedControls.some(control => control.unit == "fahrenheit");
    },
    relevantSelectedControls() {
      return this.selectedControls.filter(control => 
        this.selectedBins.find(bin => 
          bin.allControls.includes(control)
        )
      );
    },
    selectedEvents() {
      return this.events.filter(event => event.selected);
    },
    system() {
      return store.sessions[0].system;
    },
    timeSegments() {
      return this.system.timeSegments.value;
    },
    dateFrom: {
      get() {
        if (this.normalized_timeFrom == null) {
          return null;
        }
        else {
          let denormalized = denormalizeTimestamp(this.normalized_timeFrom);
          let zoned = zoneDate(denormalized * 1000, this.system.timezone.value);
          return zoned;
        }
      },
      set(value) {
        if (value == null) {
          this.normalized_timeFrom = null;
        }
        else {
          let dezoned = dezoneDate(value, this.system.timezone.value);
          this.normalized_timeFrom = normalizeRightTimestamp(dezoned.getTime() / 1000);
          this.normalized_timeTo = (this.normalized_timeTo == null) ? null : Math.max(this.normalized_timeFrom, this.normalized_timeTo);
        }
      }
    },
    dateTo: {
      get() {
        if (this.normalized_timeTo == null) {
          return null;
        }
        else {
          let denormalized = denormalizeTimestamp(this.normalized_timeTo);
          let zoned = zoneDate(denormalized * 1000, this.system.timezone.value);
          return zoned;
        }
      },
      set(value) {
        if (value == null) {
          this.normalized_timeTo = null;
        }
        else {
          let dezoned = dezoneDate(value, this.system.timezone.value);
          this.normalized_timeTo = normalizeRightTimestamp(dezoned.getTime() / 1000);
          this.normalized_timeFrom = (this.normalized_timeFrom == null) ? null : Math.min(this.normalized_timeFrom, this.normalized_timeTo);
        }
      }
    },
    timeRange() {
      return [ this.normalized_timeFrom, this.normalized_timeTo ];
    }
  },
  methods: {
    unitHtml,
    formatDate,
    recordsMenuRefresh() {
      this.$refs.recordsMenu.initMenuData();
    },
    showGraph() {
      this.setViewType("graph");
    },
    showTable() {
      this.setViewType("table");
    },
    setViewType(viewType) {
      this.viewType = viewType;
      localStorage.setItem("records.viewType", this.viewType);
    },
    isTagSelectable(databaseTag) {
      if (this.readonlyDatabase)
        return true;

      let programTag = this.tagsMappedByDatabaseId[databaseTag.id];
      return store.showAdvanced || (
        programTag != null &&                         // Hides deleted tags.
        programTag.relevant &&                        // Hides unassigned tags.
        programTag.addToStatusPage?.value !== false   // Hides moisture aggregates that are omitted from the status page.
      );
    },
    saveSelections() {
      localStorage.setItem("records.selectedTagIds", JSON.stringify([
        ... this.selectedBins.map(bin => bin.id),
        ... this.selectedControls.map(control => control.id)
      ]));
      localStorage.setItem("records.selectedTime", JSON.stringify({ begin: this.normalized_timeFrom, end: this.normalized_timeTo }));
      
      let allEventsSelected = this.events.every(event => event.selected);
      let selectedEventCodes =  allEventsSelected ? "all" : this.selectedEvents.map(event => event.eventCode);
      localStorage.setItem("records.selectedEventCodes", JSON.stringify(selectedEventCodes));

      let controlsWithColor = this.controls.filter(control => control.color != null);
      let controlColors = controlsWithColor.map(control => ({ id: control.id, color: control.color }));
      localStorage.setItem("records.controlColors", JSON.stringify(controlColors));
    },
    deleteAllRecords() {
      if (confirm("Really delete ALL RECORDS?")) {
        store.sessions[0].queueRequest("/cmd/deleteAllRecords");
        this.recordsMenuRefresh();
      }
    },
    deleteSelectedRecords() {
      if (confirm("Really delete the selected records?")) {
        let selectedBins = this.bins.filter(bin => bin.selected);
        let getSelectedTagIds = (tags) => tags.filter(tag => tag.selected).map(tag => tag.id);
        let selectedTagIds = [
          ... getSelectedTagIds(selectedBins),
          ... getSelectedTagIds(selectedBins.flatMap(bin => bin.aggrs)),
          ... getSelectedTagIds(selectedBins.flatMap(bin => bin.aggrs.flatMap(aggr => aggr.sensors))),
          ... getSelectedTagIds(selectedBins.flatMap(bin => bin.outputs))
        ];
        let selectedEventCodes = this.events.filter(event => event.selected).map(event => event.eventCode);
        store.sessions[0].queueRequest("/cmd/deleteSelectedRecords", {
          tagIds: selectedTagIds,
          eventCodes: selectedEventCodes,
          deleteFrom: this.normalized_timeFrom,
          deleteTo: this.normalized_timeTo
        });
        this.recordsMenuRefresh();
      }
    },
    deleteArchivedRecords() {
      if (confirm("Really delete all archived records?")) {
        // Determine the list of archived tags by mapping each database tag entry to it's corresponding tag 
        // in the current program configuration. If none can be found, or if one is found but it's not 
        // relevant (an unassigned i/o) then it's an archived tag.
        let tagIds = this.tags.map(tag => tag.tagId); // TODO: Why is this .tagId and not .id?
        let archivedTagIds = tagIds.filter(tagId => {
          let programTag = this.tagsMappedByDatabaseId[tagId];
          return !programTag?.relevant;
        });
        store.sessions[0].queueRequest("/cmd/deleteSelectedTagsFromRecords", { tagIds: archivedTagIds });
        this.recordsMenuRefresh();
      }
    }
  },
  watch: {
    selectedBins() {
      this.saveSelections();
    },
    relevantSelectedControls() {
      this.saveSelections();
      
      let controls = this.bins.flatMap(bin => bin.allControls);
      controls.forEach(control => {
        let selected = this.relevantSelectedControls.includes(control);
        if (selected && control.color == null) {
          let colors = this.controls.filter(control => control.color != null).map(control => control.color);
          control.color = generateColor(colors);
        }
      });
    },
    selectedEvents() {
      this.saveSelections();
    },
    timeRange() {
      this.saveSelections();
    }
  }
}

function formatDate(date) {
  let days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  let months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  let hours = date.getUTCHours();
  let hours12 = (hours) ? (hours <= 12 ? hours : hours - 12) : 12;
  let meridiem = (hours < 12) ? " AM" : " PM";
  let timePart = padStart(hours12, 2, "0") + ":" + padStart(date.getUTCMinutes(), 2, "0") + meridiem;
  let datePart = days[date.getUTCDay()] + " " + months[date.getUTCMonth()] + " " + date.getUTCDate() + ", " + date.getUTCFullYear();
  return timePart + " " + datePart;
}
</script>

<style scoped>
.records {
  background: whitesmoke;
}
.view-component {
  flex-grow: 1;
  min-width: 0;
  background: white;
  border-radius: 5px;
  border-top: 1px solid #ddd;
  box-shadow: 0 3px 3px #ccc,  0 3px 2px #ccc;
  padding: 10px;
}

.flex {
  display: flex;
  align-items: center;
}
.flex-justify-center {
  justify-content: center;
}
.flex-grow {
  flex-grow: 1;
}
.readonly-warning {
  margin: 5px;
  padding: 5px;
  color: #444;
  text-align: center;
  background: var(--orange-200);
  border-radius: 10px;
}
</style>